/**
 * FiltersDialog — manage the account's filters. Lists rules with enable toggle +
 * delete, and a single-condition / single-action create form (covers the common
 * case; multi-condition rules are a follow-up). Filters apply Fias-side over
 * newly-synced mail.
 */

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Trash2, Plus } from 'lucide-react';
import type { MailboxFilterCondition, MailboxFilterAction } from '@/lib/api';
import {
  useEmailFilters,
  useCreateFilter,
  useToggleFilter,
  useDeleteFilter,
} from '../hooks/useFilterRules';
import { useEmailCategories } from '../hooks/useMailbox';
import { useDialogAccount } from '../hooks/useDialogAccount';
import { DialogAccountPicker } from './DialogAccountPicker';

interface FiltersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Seed account; the dialog has its own in-popup picker to switch. */
  defaultAccountId?: string;
}

const FIELDS: MailboxFilterCondition['field'][] = ['from', 'to', 'subject', 'body'];
const OPERATORS: MailboxFilterCondition['operator'][] = [
  'contains',
  'not_contains',
  'equals',
  'starts_with',
  'ends_with',
  'matches_regex',
];
const ACTIONS: MailboxFilterAction['type'][] = [
  'mark_read',
  'mark_starred',
  'archive',
  'trash',
  'set_category',
];

/** Actions that take a free-text value (others ignore it). */
const VALUED_ACTIONS = new Set<MailboxFilterAction['type']>(['set_category']);

export function FiltersDialog({ open, onOpenChange, defaultAccountId }: FiltersDialogProps) {
  const { accounts, selectedAccountId, setSelectedAccountId } = useDialogAccount(defaultAccountId);
  const { data: filters, isLoading } = useEmailFilters(open ? selectedAccountId : undefined);
  const { data: categories } = useEmailCategories(open);
  const create = useCreateFilter(selectedAccountId ?? '');
  const toggle = useToggleFilter(selectedAccountId ?? '');
  const del = useDeleteFilter(selectedAccountId ?? '');

  const [name, setName] = useState('');
  const [field, setField] = useState<MailboxFilterCondition['field']>('from');
  const [operator, setOperator] = useState<MailboxFilterCondition['operator']>('contains');
  const [value, setValue] = useState('');
  const [actionType, setActionType] = useState<MailboxFilterAction['type']>('mark_read');
  const [actionValue, setActionValue] = useState('');

  const add = () => {
    if (!name.trim() || !value.trim()) return;
    create.mutate(
      {
        name: name.trim(),
        conditions: [{ field, operator, value: value.trim() }],
        actions: [
          { type: actionType, value: VALUED_ACTIONS.has(actionType) ? actionValue.trim() : '' },
        ],
      },
      {
        onSuccess: () => {
          setName('');
          setValue('');
          setActionValue('');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Filters</DialogTitle>
        </DialogHeader>
        {!selectedAccountId ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Connect an account to manage filters.
          </p>
        ) : (
          <div className="space-y-4">
            <DialogAccountPicker
              accounts={accounts}
              value={selectedAccountId}
              onChange={setSelectedAccountId}
            />
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : !filters || filters.length === 0 ? (
              <p className="text-sm text-muted-foreground">No filters yet.</p>
            ) : (
              <ul className="space-y-1">
                {filters.map((f) => (
                  <li
                    key={f.filterId}
                    className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{f.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {f.conditions[0]?.field} {f.conditions[0]?.operator} “
                        {f.conditions[0]?.value}” → {f.actions.map((a) => a.type).join(', ')}
                      </span>
                    </span>
                    <span className="flex items-center gap-1 shrink-0">
                      <label className="flex items-center gap-1 text-xs text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={f.isEnabled}
                          onChange={(e) =>
                            toggle.mutate({ filterId: f.filterId, isEnabled: e.target.checked })
                          }
                        />
                        on
                      </label>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Delete"
                        onClick={() => del.mutate(f.filterId)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="space-y-2 border-t pt-3">
              <Input
                placeholder="Filter name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">If</span>
                <select
                  className="h-8 rounded-md border bg-background px-2"
                  value={field}
                  onChange={(e) => setField(e.target.value as MailboxFilterCondition['field'])}
                >
                  {FIELDS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
                <select
                  className="h-8 rounded-md border bg-background px-2"
                  value={operator}
                  onChange={(e) =>
                    setOperator(e.target.value as MailboxFilterCondition['operator'])
                  }
                >
                  {OPERATORS.map((o) => (
                    <option key={o} value={o}>
                      {o.replace('_', ' ')}
                    </option>
                  ))}
                </select>
                <Input
                  className="w-40"
                  placeholder="value"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">then</span>
                <select
                  className="h-8 rounded-md border bg-background px-2"
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value as MailboxFilterAction['type'])}
                >
                  {ACTIONS.map((a) => (
                    <option key={a} value={a}>
                      {a.replace('_', ' ')}
                    </option>
                  ))}
                </select>
                {actionType === 'set_category' && (
                  <select
                    className="h-8 w-44 rounded-md border bg-background px-2"
                    aria-label="Category"
                    value={actionValue}
                    onChange={(e) => setActionValue(e.target.value)}
                  >
                    <option value="">Choose category…</option>
                    {(categories ?? []).map((c) => (
                      <option key={c.categoryId} value={c.categoryId}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <Button
                size="sm"
                className="gap-1"
                disabled={!name.trim() || !value.trim() || create.isPending}
                onClick={add}
              >
                <Plus className="h-3.5 w-3.5" /> Add filter
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
